import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { getUrl } from '@js/common/getUrlByDomain'

import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

import { useDocumentLoaded } from '@f-fertighaus/utils/useDocumentLoaded'

import RefreshMapCTA from './RefreshMapCTA'

const getPinColor = (id, activeID) =>
  ({
    fertighaus: id === activeID ? '#2cd7aa' : '#3b4768',
    massivhaus: id === activeID ? '#f2a71b' : '#263f46',
    bungalow: id === activeID ? '#e1b254' : '#254138',
    einfamilienhaus: id === activeID ? '#c1202b' : '#051941',
    immonet: id === activeID ? '#009acf' : '#6e6e7c',
    bauen: id === activeID ? '#016f70' : '#063b73',
    immowelt: id === activeID ? '#ffcc00' : '#303038'
  }[window.__DOMAIN__])

const MapView = ({
  actions: {
    updateQuery,
    applyQuery,
    setActiveIDonMap,
    setMapClicked,
    setLocationParams
  },
  data: {
    apiResponse: { results = [] },
    activeID,
    showApplyQueryCTA,
    locationParams,
    params,
    displayingFilters
  }
}) => {
  const [mapInitialised, setMapInitialised] = useState(false)
  const documentLoaded = useDocumentLoaded()
  const map = useRef(null)
  const mapElement = useRef(null)

  const parser = new DOMParser()

  const initiateGoogleMap = () => {
    if (documentLoaded) {
      map.current = new google.maps.Map(mapElement.current, {
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM
        },
        center: {
          lat: 51.1657,
          lng: 10.4515
        },
        zoom: 7,
        mapId: window.SherlockContainer?.environment?.GM_MAP_ID
      })

      map.current.addListener('click', () => {
        setMapClicked(true)
      })

      map.current.addListener('dragend', () => {
        setLocationParams({})
      })

      const centerMapToSpecificRanges = () => {
        if (
          params.lat__range &&
          params.lon__range &&
          !locationParams.location
        ) {
          try {
            const mapSWBounds = new google.maps.LatLng(
              Number(params.lat__range.split(',')[0]),
              Number(params.lon__range.split(',')[0])
            )
            const mapNEBounds = new google.maps.LatLng(
              Number(params.lat__range.split(',')[1]),
              Number(params.lon__range.split(',')[1])
            )
            const mapBounds = new google.maps.LatLngBounds(
              mapSWBounds,
              mapNEBounds
            )

            map.current.fitBounds(mapBounds, 0)
            applyQuery()
          } catch (e) {}
        }
      }

      centerMapToSpecificRanges()

      google.maps.event.addListenerOnce(map.current, 'tilesloaded', () => {
        setMapInitialised(true)
      })
    }
  }

  useLayoutEffect(initiateGoogleMap, [documentLoaded])

  const setPositionOnLocationChange = () => {
    if (mapInitialised && locationParams.location) {
      map.current.setCenter(locationParams.location)
      map.current.setZoom(12)
    }
  }

  useEffect(setPositionOnLocationChange, [locationParams, mapInitialised])

  const reflectMapRangeInQueryParams = () => {
    if (mapInitialised) {
      map.current.addListener('idle', () => {
        const mapBounds = map.current.getBounds()
        const mapSWBounds = mapBounds.getSouthWest()
        const mapNEBounds = mapBounds.getNorthEast()
        const queryParams = {
          lat__range: `${mapSWBounds.lat()},${mapNEBounds.lat()}`,
          lon__range: `${mapSWBounds.lng()},${mapNEBounds.lng()}`
        }
        updateQuery(queryParams)
      })
    }
  }
  useEffect(reflectMapRangeInQueryParams, [mapInitialised])

  const generateIcon = ({ type, id }) => {
    let iconSVG = `<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path d="M12 21a9 9 0 100-18 9 9 0 000 18z" stroke="${getPinColor(
      id,
      activeID
    )}" stroke-width="6"/></svg>`

    if (type === 'house_vendor') {
      iconSVG = `<svg height="60" width="44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 90"><path fill-rule="evenodd" clip-rule="evenodd" d="M66 33c0 27-33 57-33 57S0 60 0 33C0 14.775 14.775 0 33 0s33 14.775 33 33zM33 48c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15z" fill="${getPinColor(
        id,
        activeID
      )}"/><path d="M33 58c13.255 0 24-10.745 24-24S46.255 10 33 10 9 20.745 9 34s10.745 24 24 24z" fill="#fff"/><path d="M50.218 44.938L49.046 20h-7.617l-.834 24.938H38.39l.44-13.125h-3.568v-5.688l-9.25 5.688v-5.688l-9.692 5.688v13.125H15V48h37v-3.062h-1.782zm-26.849-4.375h-3.083v-3.062h3.083v3.062zm5.285 0h-3.083v-3.062h3.083v3.062zm5.287 0h-3.084v-3.062h3.084v3.062z" fill="${getPinColor(
        id,
        activeID
      )}"/></svg>`
    } else if (type === 'sales_profile') {
      iconSVG = `<svg height="60" width="44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 90"><path fill-rule="evenodd" clip-rule="evenodd" d="M66 33c0 27-33 57-33 57S0 60 0 33C0 14.775 14.775 0 33 0s33 14.775 33 33zM33 48c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15z" fill="${getPinColor(
        id,
        activeID
      )}"/><path d="M33 58c13.255 0 24-10.745 24-24S46.255 10 33 10 9 20.745 9 34s10.745 24 24 24z" fill="#fff"/><path fill-rule="evenodd" clip-rule="evenodd" d="M28.956 44.592l3.656 3.699-5.7 10.209h12.126l-5.602-10.209 3.642-3.696a31.1 31.1 0 0114.144 4.873 3 3 0 011.272 2.265l.006.195V59.5h-39v-7.572a3 3 0 011.278-2.46 31.101 31.101 0 0114.178-4.876zM33 14.5c5.799 0 10.5 4.701 10.5 10.5S38.799 35.5 33 35.5 22.5 30.799 22.5 25 27.201 14.5 33 14.5z" fill="${getPinColor(
        id,
        activeID
      )}"/></svg>`
    } else if (type === 'model_house') {
      iconSVG = `<svg width="44" height="60" viewBox="0 0 50 66" fill="${getPinColor(
        id,
        activeID
      )}" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_534_2906)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M49.0625 24.4053C49.0625 44.0928 25 65.9678 25 65.9678C25 65.9678 0.9375 44.0928 0.9375 24.4053C0.9375 11.1159 11.7106 0.342773 25 0.342773C38.2894 0.342773 49.0625 11.1159 49.0625 24.4053ZM25 35.3428C31.0406 35.3428 35.9375 30.4459 35.9375 24.4053C35.9375 18.3647 31.0406 13.4678 25 13.4678C18.9594 13.4678 14.0625 18.3647 14.0625 24.4053C14.0625 30.4459 18.9594 35.3428 25 35.3428Z" fill="inherit"/>
        <path d="M25 42.6344C34.665 42.6344 42.5 34.7994 42.5 25.1344C42.5 15.4694 34.665 7.6344 25 7.6344C15.335 7.6344 7.5 15.4694 7.5 25.1344C7.5 34.7994 15.335 42.6344 25 42.6344Z" fill="white"/>
        <g clip-path="url(#clip1_534_2906)">
        <path d="M24.5427 10.4526C24.796 10.2019 25.204 10.2019 25.4573 10.4526L37.7328 22.6022C38.1455 23.0107 37.8562 23.7143 37.2755 23.7143H12.7245C12.1438 23.7143 11.8545 23.0107 12.2672 22.6022L24.5427 10.4526Z" fill="inherit"/>
        <path d="M14.3195 23.3818H22.7143V34.5195C22.7143 34.9785 22.3422 35.3506 21.8832 35.3506H15.1507C14.6916 35.3506 14.3195 34.9785 14.3195 34.5195V23.3818Z" fill="inherit"/>
        <path d="M27.1195 23.3818H35.5143V34.5195C35.5143 34.9785 35.1422 35.3506 34.6832 35.3506H27.9507C27.4916 35.3506 27.1195 34.9785 27.1195 34.5195V23.3818Z" fill="inherit"/>
        <rect x="22.7143" y="23.3818" width="4.40519" height="4.40519" fill="inherit"/>
        </g>
        </g>
        <defs>
        <clipPath id="clip0_534_2906">
        <rect width="50" height="66" fill="white"/>
        </clipPath>
        <clipPath id="clip1_534_2906">
        <rect width="26" height="26" fill="white" transform="translate(12 10)"/>
        </clipPath>
        </defs>
        </svg>`
    }

    return iconSVG
  }

  const renderPins = () => {
    markers.map(marker => marker.setMap(null))

    if (results.length > 0) {
      const newMarkers = []
      results.map(office => {
        const position = new google.maps.LatLng(office.lat, office.lon)
        const icon = generateIcon({
          type: office.highlight_map ? office.location_type : null,
          id: office.nid,
          nid: office.nid
        })
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position,
          map: map.current,
          ...(document.documentMode
            ? {}
            : {
                content: parser.parseFromString(icon, 'image/svg+xml')
                  ?.documentElement
              }),
          title: office.vendor_name,
          gmpClickable: true
        })

        marker.setAttribute('id', office.nid)
        marker.setAttribute('pinType', office.location_type)
        marker.setAttribute('isPremium', office.highlight_map)
        marker.setAttribute('isSelected', office.nid === activeID)

        marker.addListener('click', () => {
          setActiveIDonMap(office.nid)
        })

        newMarkers.push(marker)
      })
      setMarkers(newMarkers)
    }
  }

  const handleActivePin = () => {
    for (const marker of markers) {
      if (
        marker.getAttribute('id') === activeID ||
        marker.getAttribute('isSelected') === 'true'
      ) {
        marker.setAttribute(
          'isSelected',
          marker.getAttribute('id') === activeID
        )
        if (!document.documentMode) {
          const newIcon = generateIcon({
            type:
              marker.getAttribute('isPremium') === 'true'
                ? marker.getAttribute('pinType')
                : null,
            label: marker.title,
            id: marker.getAttribute('id')
          })
          marker.content = parser.parseFromString(
            newIcon,
            'image/svg+xml'
          )?.documentElement
        }
        marker.zIndex = +marker.zIndex + 20
      }
    }
  }

  useEffect(() => {
    handleActivePin()
  }, [activeID])

  const [markers, setMarkers] = useState([])
  useLayoutEffect(() => {
    renderPins()
  }, [results])

  return (
    <div className='[ f-pos-rel ]' style={{ height: '100%' }}>
      <div ref={mapElement} style={{ height: '100%' }}></div>
      <RefreshMapCTA
        applyQuery={applyQuery}
        displayingFilters={displayingFilters}
        showApplyQueryCTA={showApplyQueryCTA}
      />
      <span className='[ f-pos-ab f-hidden:s ] MapView-list-cta-wrap'>
        <CTA
          type='block-secondary'
          tag='a'
          href={`${getUrl('vendorList')()}`}
          className='[ f-p-v-010 f-p-h-020 ] is-inverted MapView-map-cta'>
          Anbieterliste
          <Icon
            icon='grid-list-16px'
            className='[ f-icon-020 f-c-st-inherit f-icon-t-mini f-m-l-010 ]'
          />
        </CTA>
      </span>
    </div>
  )
}

export default MapView
